import React, { useCallback, useMemo } from 'react';
import {
  AppButton,
  Box,
  Image,
  Inscription,
  LargeCloseIcon,
  LinkIcon,
  Preloader,
  RawButton,
  TextWithParagraphs,
} from '@common-fe/common-fe';

import GridBackground from '@/assets/new-reports/grid-background.svg';
import { ROUTES } from '@/common';
import { LARGE_SIZE_LOGO, LetterIconSizeEnum } from '@/common/constants';
import { Icon } from '@/components/elements/Icon/Icon';
import ModalWrapper from '@/components/wrappers/ModalWrapper';
import { useHistory } from '@/modules/core/hooks';
import { Copay } from '@/modules/ExpressSetup/ExpressSetup.types';
import { useGetOrgFullAddress } from '@/modules/ExpressSetup/utils/getOrgFullAddress.ts';
import { FeatureFlags } from '@/modules/featureFlags/featureFlags.constants';
import { useFeatureFlag } from '@/modules/featureFlags/hooks/useFeatureFlag';
import { CopaysSummaryContainer } from '@/modules/Renewals/RenewalSetup/CopaysStep/CopaysSummaryContainer';
import { PlansSummaryContainer } from '@/modules/Renewals/RenewalSetup/PlanYearPlansStep/PlansSummaryContainer';

import { HealthPlanRenewal, Renewal, RenewalStatus } from '../../renewals.types';
import HealthPlanSummaryCard from '../components/HealthPlanSummaryCard';
import CanceledItem from '../CopaysStep/CanceledItem';
import {
  CopaysOptionValues,
  RenewalPlanOptionValues,
  RenewalSetupStep,
  RenewalSetupStepTitle,
} from '../RenewalSetup.types';
import RenewalSetupLinks from '../RenewalSetupLinks';
import { useCopayStore, useHealthPlansStore, useRenewalSetupStore } from '../stores';
import { StepOption } from '../stores/useRenewalSetup.store';
import { getStepCounter } from '../utils';

import ContainerItem from './ContainerItem';
import NextSteps from './NextSteps';
import PlansNoteDetails from './PlansNoteDetails';

interface Props {
  isVisible: boolean;
  isLoading?: boolean;
  renewalSetupData?: Renewal;
  steps?: RenewalSetupStep[];
  isViewMode?: boolean;
  showPlansButton?: boolean;
  onSetVisible: (value: boolean) => void;
  onSave?: () => void;
  handleStepName?: (stepName: RenewalSetupStep) => void;
}

const CANCELED_COUNTS_STEPS_LABEL = '2/2';
const getPlansBlock = (
  plansPlanYearSelectedOption?: StepOption,
  renewalSetupData?: Renewal,
  isViewMode?: boolean,
  onEditPlans?: () => void
) => {
  if (
    plansPlanYearSelectedOption === RenewalPlanOptionValues.CANCELED ||
    (renewalSetupData?.status === RenewalStatus.CANCELED && isViewMode)
  ) {
    return (
      <Box gap="s">
        <Box round="container1Round" align="center" background="dangerContainer" pad="l">
          <Inscription size="large" weight="bold" lineHeight="22px">
            {renewalSetupData?.status === RenewalStatus.CANCELED
              ? `You've chosen not to renew your plans. Your reasoning is listed below. ${renewalSetupData?.partnerName || ''} will reach out shortly.`
              : `You've chosen not to renew your plans. Your reasoning is listed below and will be sent to ${renewalSetupData?.partnerName || ''} upon confirmation.`}
          </Inscription>
        </Box>
        <ContainerItem title={RenewalSetupStepTitle.plansPlanYear} isSuccessMode={false} onEdit={onEditPlans}>
          <PlansNoteDetails
            isCanceled={renewalSetupData?.status === RenewalStatus.CANCELED}
            planRenewalNote={renewalSetupData?.planRenewalNote}
          />
        </ContainerItem>
      </Box>
    );
  }

  if (
    plansPlanYearSelectedOption === RenewalPlanOptionValues.ACTIVE ||
    plansPlanYearSelectedOption === RenewalPlanOptionValues.REQUESTED ||
    (renewalSetupData?.status === RenewalStatus.REQUESTED && isViewMode)
  ) {
    return (
      <Box gap="s">
        <Box round="container1Round" align="center" background="successContainer" pad="l">
          <Inscription size="large" weight="bold" lineHeight="22px">
            {renewalSetupData?.status === RenewalStatus.REQUESTED
              ? "Your request to renew your plan has been sent to your account manager. They will be in touch to confirm the details and complete your plan's renewal."
              : "You've chosen to renew your plan(s). Your selected configuration is listed below and will be applied upon confirmation."}
          </Inscription>
        </Box>
        <ContainerItem title={RenewalSetupStepTitle.plansPlanYear} isSuccessMode onEdit={onEditPlans}>
          {plansPlanYearSelectedOption === RenewalPlanOptionValues.ACTIVE && (
            <PlansSummaryContainer
              planYear={renewalSetupData?.newPlanYear}
              plans={renewalSetupData?.plans}
              isViewMode={isViewMode}
            />
          )}
          {(plansPlanYearSelectedOption === RenewalPlanOptionValues.REQUESTED ||
            (renewalSetupData?.status === RenewalStatus.REQUESTED && isViewMode)) && (
            <PlansNoteDetails
              planYear={renewalSetupData?.newPlanYear}
              isRequested={renewalSetupData?.status === RenewalStatus.REQUESTED}
              planRenewalNote={renewalSetupData?.planRenewalNote}
            />
          )}
        </ContainerItem>
      </Box>
    );
  }

  return null;
};

const getCopaysBlock = (
  plansPlanYearSelectedOption?: StepOption,
  copaysSelectedOption?: StepOption,
  renewalSetupData?: Renewal,
  copays?: Copay[],
  isViewMode?: boolean,
  onEditCopays?: () => void
) => {
  if (
    plansPlanYearSelectedOption === RenewalPlanOptionValues.CANCELED ||
    renewalSetupData?.status === RenewalStatus.CANCELED
  )
    return null;
  return (
    <ContainerItem
      title={RenewalSetupStepTitle.copays}
      isSuccessMode={
        copaysSelectedOption === CopaysOptionValues.REQUESTED ||
        copaysSelectedOption === CopaysOptionValues.ACTIVE ||
        (!!renewalSetupData?.copays?.length && !!isViewMode)
      }
      onEdit={onEditCopays}
    >
      {(copaysSelectedOption === CopaysOptionValues.ACTIVE ||
        (renewalSetupData?.status === RenewalStatus.REQUESTED && isViewMode)) && (
        <CopaysSummaryContainer copays={renewalSetupData?.copays} />
      )}
      {copaysSelectedOption === CopaysOptionValues.REQUESTED && <CopaysSummaryContainer copays={copays} />}
      {copaysSelectedOption === CopaysOptionValues.CANCELED && (
        <CanceledItem planYear={renewalSetupData?.newPlanYear} />
      )}
    </ContainerItem>
  );
};

const getHealthPlansBlock = (
  plansPlanYearSelectedOption?: StepOption,
  healthPlansSelectedOption?: StepOption,
  renewalSetupData?: Renewal,
  healthPlans?: HealthPlanRenewal[],
  healthPlanDetailsNote?: string,
  isViewMode?: boolean,
  onEditHealthPlans?: () => void
) => {
  if (
    plansPlanYearSelectedOption === RenewalPlanOptionValues.CANCELED ||
    renewalSetupData?.status === RenewalStatus.CANCELED
  ) {
    return null;
  }

  const healthPlansData = healthPlans?.length ? healthPlans : renewalSetupData?.healthPlans;

  if (healthPlansSelectedOption === RenewalPlanOptionValues.ACTIVE) {
    return (
      <ContainerItem title={RenewalSetupStepTitle.healthPlans} isSuccessMode onEdit={onEditHealthPlans}>
        <Box gap="spacing12">
          {(healthPlansData || []).map((healthPlan, index) => (
            <HealthPlanSummaryCard
              key={`${healthPlan.name}-${index}`}
              healthPlan={healthPlan}
              testId={`renewal-modal-health-plan-card-${index}`}
              showEditButtons={false}
              showEditedPlanCode={!isViewMode}
            />
          ))}
        </Box>
      </ContainerItem>
    );
  }

  if (healthPlansSelectedOption === RenewalPlanOptionValues.REQUESTED) {
    return (
      <ContainerItem title={RenewalSetupStepTitle.healthPlans} isSuccessMode onEdit={onEditHealthPlans}>
        <Box
          background="canvas"
          pad={{ horizontal: 'spacing24', vertical: 'spacing16' }}
          round="container1Round"
          border={{ color: 'border1' }}
        >
          <Inscription color="textBody" margin={{ bottom: 'spacing4' }} weight="bold">
            Renewal details:
          </Inscription>
          <TextWithParagraphs text={healthPlanDetailsNote || ''} />
        </Box>
      </ContainerItem>
    );
  }

  if (healthPlansSelectedOption === RenewalPlanOptionValues.CANCELED) {
    return (
      <ContainerItem
        title={RenewalSetupStepTitle.healthPlans}
        isSuccessMode={false}
        onEdit={onEditHealthPlans}
      >
        <Box
          background="canvas"
          pad={{ horizontal: 'spacing24', vertical: 'spacing16' }}
          round="container1Round"
          border={{ color: 'border1' }}
        >
          <Inscription color="textBody" margin={{ bottom: 'spacing4' }} weight="bold">
            Reason for not offering health plan deductible tracking in the new year:
          </Inscription>
          <TextWithParagraphs text={healthPlanDetailsNote || ''} />
        </Box>
      </ContainerItem>
    );
  }

  // View mode fallback (banners): show health plans from renewalSetupData when store is empty
  if (isViewMode && healthPlansData?.length) {
    return (
      <ContainerItem title={RenewalSetupStepTitle.healthPlans} isSuccessMode onEdit={onEditHealthPlans}>
        <Box gap="spacing12">
          {healthPlansData.map((healthPlan, index) => (
            <HealthPlanSummaryCard
              key={`${healthPlan.name}-${index}`}
              healthPlan={healthPlan}
              testId={`renewal-modal-health-plan-card-${index}`}
              showEditButtons={false}
            />
          ))}
        </Box>
      </ContainerItem>
    );
  }

  return null;
};

const RenewalSetupModal: React.FC<Props> = ({
  isVisible,
  isLoading,
  renewalSetupData,
  steps,
  isViewMode,
  showPlansButton,
  onSetVisible,
  onSave,
  handleStepName,
}) => {
  const history = useHistory();
  const isHealthPlanRenewalEnabled = useFeatureFlag(FeatureFlags.HEALTH_PLAN_RENEWAL);
  const fullAddress = useGetOrgFullAddress(renewalSetupData?.address, true);
  const copaysSelectedOption = useRenewalSetupStore((state) => state.copays.selectedOption);
  const plansPlanYearSelectedOption = useRenewalSetupStore((state) => state.plansPlanYear.selectedOption);
  const healthPlansSelectedOption = useRenewalSetupStore((state) => state.healthPlans.selectedOption);
  const healthPlanDetailsNote = useRenewalSetupStore((state) => state.healthPlanDetailsNote);
  const copays = useCopayStore((state) => state.state.copays);
  const healthPlans = useHealthPlansStore((state) => state.state.healthPlans);
  const handleEditPlans = useCallback(() => {
    onSetVisible(false);
    handleStepName?.(RenewalSetupStep.PlansPlanYear);
  }, [onSetVisible, handleStepName]);

  const handleEditCopays = useCallback(() => {
    onSetVisible(false);
    handleStepName?.(RenewalSetupStep.Copays);
  }, [onSetVisible, handleStepName]);

  const handleEditHealthPlans = useCallback(() => {
    onSetVisible(false);
    handleStepName?.(RenewalSetupStep.HealthPlans);
  }, [onSetVisible, handleStepName]);
  const submitButtonCountSteps = useMemo(
    () =>
      plansPlanYearSelectedOption === RenewalPlanOptionValues.CANCELED
        ? CANCELED_COUNTS_STEPS_LABEL
        : getStepCounter(steps || [], RenewalSetupStep.RenewalPopup),
    [plansPlanYearSelectedOption, steps]
  );

  const handleOpenPlans = useCallback(() => {
    window.open(`${ROUTES.PLANS}/${history.location.search}`, '_blank');
  }, [history.location.search]);

  return (
    <ModalWrapper
      data-testid="renewal-setup-activate-modal"
      contentStyle={{ padding: 0, overflow: 'hidden' }}
      visible={isVisible}
      onSetVisible={onSetVisible}
    >
      <Box align="center" style={{ position: 'relative', overflow: 'hidden' }} pad="l">
        <Image
          src={GridBackground}
          style={{
            position: 'absolute',
            left: -20,
            top: -20,
            zIndex: 1,
          }}
        />
        <Box style={{ zIndex: 2 }}>
          <Box direction="row" align="center" justify="end">
            <RawButton
              data-testid="renewal-modal_close_button"
              style={{ height: '24px' }}
              onClick={() => onSetVisible(false)}
            >
              <LargeCloseIcon color="iconPrimary" size="24px" />
            </RawButton>
          </Box>
          <Box margin={{ bottom: 'xxl', top: 'm' }} data-testid="renewal-setup-topbar-logo" align="center">
            <Icon
              size={LetterIconSizeEnum.LOGO}
              text="name"
              height={LARGE_SIZE_LOGO.height}
              width={LARGE_SIZE_LOGO.width}
              src={renewalSetupData?.logo}
            />
          </Box>
          <Inscription
            margin={{ bottom: 'm' }}
            size="54px"
            color="textTitle"
            weight="bold"
            textAlign="center"
          >
            {renewalSetupData?.organizationName || ''}
          </Inscription>
          <Inscription margin={{ bottom: 'l' }} textAlign="center">
            {fullAddress}
          </Inscription>
          <Box gap="s" margin={{ bottom: 'l' }}>
            {getPlansBlock(
              plansPlanYearSelectedOption,
              renewalSetupData,
              isViewMode,
              !isViewMode ? handleEditPlans : undefined
            )}
            {isHealthPlanRenewalEnabled &&
              getHealthPlansBlock(
                plansPlanYearSelectedOption,
                healthPlansSelectedOption,
                renewalSetupData,
                healthPlans,
                healthPlanDetailsNote,
                isViewMode,
                !isViewMode ? handleEditHealthPlans : undefined
              )}
            {getCopaysBlock(
              plansPlanYearSelectedOption,
              copaysSelectedOption,
              renewalSetupData,
              copays,
              isViewMode,
              !isViewMode ? handleEditCopays : undefined
            )}
          </Box>
          {isViewMode && (
            <Inscription lineHeight="20px" color="textBody" textAlign="center" margin={{ bottom: 's' }}>
              If you want to make changes, please contact{' '}
              <Inscription weight="bold">{renewalSetupData?.partnerName}</Inscription>
            </Inscription>
          )}
          {!isViewMode && (
            <Box direction="row" width="100%" margin={{ bottom: 's' }} gap="s">
              {plansPlanYearSelectedOption === RenewalPlanOptionValues.CANCELED && (
                <AppButton
                  onClick={() => onSetVisible(false)}
                  testId="cancel-renew-popup"
                  style={{ height: '54px' }}
                  containerStyle={{ maxWidth: 'none', width: '100%' }}
                  buttonType="secondary"
                >
                  Cancel
                </AppButton>
              )}
              <AppButton
                onClick={onSave}
                disabled={isLoading}
                testId="confirm-renew-popup"
                style={{ height: '54px' }}
                containerStyle={{ maxWidth: 'none', width: '100%' }}
              >
                {isLoading ? <Preloader /> : `Confirm Configuration (${submitButtonCountSteps})`}
              </AppButton>
            </Box>
          )}
          {isViewMode && showPlansButton && renewalSetupData?.status === RenewalStatus.REQUESTED && (
            <Box direction="row" width="100%" margin={{ bottom: 's' }}>
              <AppButton
                onClick={handleOpenPlans}
                disabled={isLoading}
                testId="view-plans-link"
                style={{ height: '54px' }}
                containerStyle={{ maxWidth: 'none', width: '100%' }}
                rightIcon={<LinkIcon color="iconAccent" size="small" />}
              >
                View Plans
              </AppButton>
            </Box>
          )}
          <NextSteps renewalSetupData={renewalSetupData} />
          <Box justify="start" align="start" width="100%">
            <RenewalSetupLinks renewalSetupData={renewalSetupData} />
          </Box>
        </Box>
      </Box>
    </ModalWrapper>
  );
};

export default RenewalSetupModal;
